import { forwardRef, ReactNode } from 'react'
import {
    ControllerFieldState,
    useController,
    UseControllerProps,
} from 'react-hook-form'
import cl from 'clsx'

import { Input2, type Input2Props } from '@/shared/ui'

import classes from './ContragentModal.module.scss'
import {
    FormValues,
    isFieldRequired,
    getFieldMaxLength,
    ContragentType,
    TextFieldKey,
} from './form/schema'

type SpecificInputProps = {
    label: string

    required?: boolean
    maxLength?: number
    digitsOnly?: boolean
    prefix?: string
} & Pick<Input2Props, 'prefix' | 'withClear'>

export type GenericTextFieldProps = SpecificInputProps & {
    slotSubscript?: ReactNode
} & Pick<
        Input2Props,
        | 'name'
        | 'disabled'
        | 'onChange'
        | 'onBlur'
        | 'error'
        | 'value'
        | 'placeholder'
        | 'inputClassName'
    >
export const GenericTextField = forwardRef<
    HTMLInputElement,
    GenericTextFieldProps
>(
    (
        {
            slotSubscript,
            error,
            label,
            digitsOnly,
            onChange: onChangeProp,
            required,
            maxLength,
            value,
            placeholder,
            inputClassName,
            ...props
        },
        ref
    ) => {
        const onChange: GenericTextFieldProps['onChange'] =
            //for non-digits inputs browser handles maxLength
            onChangeProp && maxLength && digitsOnly
                ? (newValue) => {
                      if (
                          newValue.length <= maxLength ||
                          newValue.length < (value ?? '')?.toString().length
                      ) {
                          onChangeProp(newValue)
                      }
                  }
                : onChangeProp

        return (
            <label
                className={cl(classes.textField, {
                    [classes.required]: required,
                })}
            >
                <div className={classes.label}>{label}</div>
                <Input2
                    {...props}
                    {...{ value, onChange, maxLength }}
                    ref={ref}
                    placeholder={placeholder}
                    type={digitsOnly ? 'number' : 'text'}
                    inputClassName={cl(
                        error ? 'border-red-500' : '',
                        inputClassName
                    )}
                />
                {error && typeof error === 'string' && (
                    <div className="text-[10px] text-red-500">{error}</div>
                )}
                {slotSubscript}
            </label>
        )
    }
)

export type TextFieldProps = SpecificInputProps &
    Omit<UseControllerProps<FormValues>, 'name'> & {
        type: ContragentType
        name: TextFieldKey
        roleType?: 'publisher' | 'advertiser' | 'agency' | 'ors'
        RenderSubscript?: React.FC<{
            fieldState: ControllerFieldState
            // formState: object
        }>
        placeholder?: string
        inputClassName?: string
    }

export const TextField = ({
    label,
    digitsOnly,
    RenderSubscript,
    type,
    roleType,
    prefix,
    withClear,
    placeholder,
    inputClassName,
    ...controllerProps
}: TextFieldProps) => {
    const { field, fieldState, formState } = useController(controllerProps)
    const { name } = controllerProps

    /*
   name, ref, disabled
   onChange, onBlur, value

   */

    const slotSubscript = RenderSubscript ? (
        <RenderSubscript {...{ fieldState, formState }} />
    ) : undefined

    return (
        <GenericTextField
            {...field}
            {...{
                label,
                digitsOnly,
                slotSubscript,
                prefix,
                withClear,
                placeholder,
                inputClassName,
            }}
            disabled={formState.isSubmitting}
            required={isFieldRequired(type, name, roleType)}
            maxLength={getFieldMaxLength(type, name)}
            error={fieldState.error?.message}
        />
    )
}
